/**
 * THE REGISTERED DOCUMENT VOCABULARY, and a filename guess to seed the gate's first question.
 *
 * ## Why this list is mirrored rather than imported
 *
 * `packages/web` has NO workspace dependencies — React and nothing else — and that is deliberate: it
 * is a browser bundle, and R9 is a measured latency budget. Importing `@ggpathos/engine` to borrow one
 * array would pull the deterministic spine into the browser. So the array is mirrored here and
 * `document-types.guard.test.ts` fails if the two ever drift apart.
 *
 * ## 🔴 Why it had to be Chad's list and not ours
 *
 * This panel used to offer eleven types we wrote ourselves — "Offering memorandum", "Rent roll",
 * "Operating statement", "Appraisal", "Loan document". The engine's C2 check judges a document's
 * content against Chad's registered `DOC_TYPES` and compares the two **exactly**. Of the two lists,
 * **only `Proforma` and `Other` overlapped.** So:
 *
 * - eight of our eleven could never match any judged value, and
 * - "Environmental report" against "Environmental Site Assessment" would have fired a **false**
 *   "confirm the type" on two names for the same thing.
 *
 * C2 shipped 2026-08-20 and has been quiet ever since because `document_ingests.document_type` was
 * null on every row. Feeding it without first fixing the vocabulary would have turned a silent check
 * into a wrong one (ADR-0052 D4).
 *
 * ⚠️ **REGISTERED ORDER, NOT DISPLAY ORDER** — Chad's comment on the source array is explicit that the
 * UI alphabetizes at the render site. Do not sort this array; `sortedForDisplay()` returns the copy a
 * picker should show.
 *
 * ⚠️ **A known gap, recorded rather than quietly patched.** This vocabulary is construction-project
 * shaped. A deal package dropped on a project record — an offering memorandum, a rent roll, an
 * operating statement — has no better home here than `Other`. That is a canon question for Chad if it
 * bites in practice, and inventing a twentieth type locally is exactly the divergence that produced
 * the mismatch above.
 */

/** Chad's registered types, mirrored from `packages/engine/src/document-checks.ts` (`gate_store.py`). */
export const DOCUMENT_TYPES = [
  'Underwriting Analysis',
  'Market Report',
  'Proforma',
  'Cost Estimate',
  'Financing Package',
  'Tax Document',
  'Due Diligence Report',
  'Environmental Site Assessment',
  'Drawing / Spec',
  'Schedule',
  'Contract / Agreement',
  'RFP / Requirements',
  'Bid Document',
  'Subcontractor Quote',
  'Site / Survey',
  'Application / Filing',
  'Correspondence',
  'Photo / Media',
  'Other',
] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

/** The picker's order. Alphabetical, with `Other` pinned last — it is a fallback, not an O-word. */
export function sortedForDisplay(): DocumentType[] {
  const rest = DOCUMENT_TYPES.filter((t) => t !== 'Other')
    .slice()
    .sort((a, b) => a.localeCompare(b));
  return [...rest, 'Other'];
}

/**
 * 🔴 CHAD'S RULES, TRANSCRIBED FROM `pee_build/gate_store.py` `detect_type()`, IN HIS ORDER.
 *
 * Order is behaviour, not style: the rules are tried top-down and the first match wins, so moving a
 * row changes verdicts. `"2025-014 GMP Budget.pdf"` is the worked example — `gmp` sits in the contract
 * rule, above nothing that would catch "budget", so it is a `Contract / Agreement` and not a
 * `Cost Estimate`.
 *
 * ⚠️ **WE WROTE OUR OWN FIRST, AND IT DISAGREED WITH HIS ON 11 OF 26 REAL FILENAMES.** Same mistake as
 * the document-type vocabulary one function above: a registry existed and we invented beside it. His
 * docstring even reaches the same design conclusion we reasoned to independently — _"Filename-only
 * detection (Deal Analyzer precedent: honest 'confirm' chip, no silent guess)"_ — so the disagreement
 * was never about approach, only about rules nobody compared.
 *
 * The disagreements ran BOTH ways and neither list was a superset: his catches appraisal, quote,
 * geotech and takeoff, which we missed; ours catches `.mpp`, AIA numbers, ITB and correspondence,
 * which he misses. So his run FIRST and ours only where his fall through — see below.
 */
const REGISTERED_RULES: readonly (readonly [RegExp, DocumentType])[] = [
  [/\besa\b|environmental|phase i|phase 1/, 'Environmental Site Assessment'],
  [/proforma|pro forma|pro-forma/, 'Proforma'],
  [/market stud|market analys|market report|submarket|\bmarket\b|appraisal/, 'Market Report'],
  [/estimate|takeoff|take-off/, 'Cost Estimate'],
  [/financing|loan|term sheet|lender/, 'Financing Package'],
  [/\btax(es)?\b|property tax|1099|w-?9|assessor|abatement/, 'Tax Document'],
  [/schedule/, 'Schedule'],
  [/contract|agreement|gmp/, 'Contract / Agreement'],
  [/rfp|requirements/, 'RFP / Requirements'],
  [/\bbid\b/, 'Bid Document'],
  [/quote/, 'Subcontractor Quote'],
  [/drawing|spec|plan/, 'Drawing / Spec'],
  [/survey|parcel|site map|plat/, 'Site / Survey'],
  [/application|nchfa|permit/, 'Application / Filing'],
  [/feasibility|due diligence|geotech|boring|subsurface/, 'Due Diligence Report'],
  [/analysis|underwrit/, 'Underwriting Analysis'],
  [/photo|\.jpe?g$|\.png$/, 'Photo / Media'],
];

/**
 * OUR ADDITIONS, consulted only where Chad's rules found nothing.
 *
 * Strictly an extension: because these run after the registered rules, they can never overturn one of
 * his verdicts, only fill a gap he leaves as `Other`. That ordering is the entire safety property —
 * every filename he classifies, we classify identically, and the two systems cannot drift on a case
 * either has an opinion about.
 *
 * ⚠️ These are worth offering him for the registry rather than keeping privately. They are here
 * because the record contains files his rules do not reach — a `.mpp` schedule, an AIA contract by
 * number, an ITB, a `uw memo` — not because we disagree with anything he wrote.
 */
const SUPPLEMENTARY_RULES: readonly (readonly [RegExp, DocumentType])[] = [
  [/\.mpp$/, 'Schedule'],
  [/\.(dwg|dxf|rvt)$/, 'Drawing / Spec'],
  [/\.(gif|tiff?|mp4|mov)$/, 'Photo / Media'],
  [/\baia\s?[abc]\d/, 'Contract / Agreement'],
  [/\bitb\b|invitation to bid/, 'Bid Document'],
  [/\buw (memo|analysis)\b/, 'Underwriting Analysis'],
  [/\b(letter|memo|correspondence|email)\b/, 'Correspondence'],
  [/\b(zoning|entitlement|filing)\b/, 'Application / Filing'],
  [/\b(comps?|demographics?)\b/, 'Market Report'],
  [/\b(subcontract|msa|loi)\b/, 'Contract / Agreement'],
  [/\bbudget\b/, 'Cost Estimate'],
];

/**
 * A guess at a document's type from its filename, or `null` when nothing matched.
 *
 * 🔴 **THIS IS A SEED, NOT A DECLARATION, AND THE DIFFERENCE IS THE WHOLE DESIGN.** Canon R10 asks for
 * a "confirm the auto-detected type (honest 'confirm' chip pattern)"; canon R6 forbids silent saves.
 * Both hold because **the person still declares** — the guess pre-fills the box, the confirm is the
 * declaration, and nothing is submitted until they make it.
 *
 * ⚠️ And it must stay a FILENAME guess rather than a model call, or C2 becomes a mirror. C2 compares
 * the human's declared type against the type a model judged **from the content**. Seed the declaration
 * with the model's own judgment and the check compares it to itself and can never disagree. The
 * filename and the content are genuinely independent sources, which is what makes C2 informative.
 *
 * ⚠️ **`null`, where Chad's `detect_type` returns `"Other"`.** The one deliberate difference, and it is
 * about the UI rather than the classification: a pre-filled box needs only a click to accept, so
 * defaulting an unrecognised file to `Other` makes "file everything as Other" the path of least
 * resistance. `null` leaves the box empty and no Confirm button to press, so the person has to choose.
 * `Other` remains selectable — it is an honest answer when a person means it, and junk when a machine
 * guessed it.
 */
export function suggestDocumentType(filename: string): DocumentType | null {
  // Chad's rules read a LOWERCASED raw filename — his `\.jpe?g$` anchors on the real extension, so
  // normalising separators first would break it. Ours read the separator-flattened name, because
  // `_` is a word character and `\bproforma\b` does not match `GGP_Proforma_Commons.xlsm`.
  const raw = filename.toLowerCase();
  for (const [pattern, type] of REGISTERED_RULES) {
    if (pattern.test(raw)) return type;
  }
  const normalised = raw.replace(/[._-]+/g, ' ');
  for (const [pattern, type] of SUPPLEMENTARY_RULES) {
    if (pattern.test(raw) || pattern.test(normalised)) return type;
  }
  return null;
}
